import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Gauge, Factory } from 'lucide-react';
import { API } from '../../config';

const fmtMt = (n) =>
  Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

const barColor = (pct) => {
  if (pct >= 90) return 'bg-emerald-500';
  if (pct >= 70) return 'bg-blue-500';
  if (pct >= 50) return 'bg-amber-500';
  return 'bg-red-500';
};

const PlantProgressCard = ({ row }) => {
  const monthly = Number(row.monthly_capacity_mt || 0);
  const mtd = Number(row.mtd_production_mt || 0);
  const expected = Number(row.expected_mt_by_today || 0);
  const utilMonth = Number(row.utilization_pct_of_month || 0);
  const utilExpected = Number(row.utilization_pct_of_expected || 0);

  // Bar width capped at 100% visually; overflow shown by badge
  const widthMonth = Math.min(100, utilMonth);
  const expectedMarkerPct =
    monthly > 0 ? Math.min(100, (expected / monthly) * 100) : 0;

  return (
    <div
      className="bg-white rounded-lg shadow-md p-3 sm:p-4 border border-gray-100"
      data-testid={`capacity-progress-card-${row.plant_id}`}
    >
      <div className="flex items-start justify-between mb-3 gap-2">
        <div className="flex items-center min-w-0">
          <Factory className="h-5 w-5 mr-2 text-[#156ACA] flex-shrink-0" />
          <div className="min-w-0">
            <h4 className="text-sm sm:text-base font-bold text-gray-900 truncate">
              {row.plant_name}
            </h4>
            <p className="text-[11px] sm:text-xs text-gray-500">
              Day {row.days_elapsed_in_month} / {row.days_in_month}
            </p>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div
            className={`text-base sm:text-lg font-bold ${
              utilMonth >= 90
                ? 'text-emerald-600'
                : utilMonth >= 70
                  ? 'text-blue-600'
                  : utilMonth >= 50
                    ? 'text-amber-600'
                    : 'text-red-600'
            }`}
            data-testid={`capacity-progress-pct-${row.plant_id}`}
          >
            {utilMonth.toFixed(1)}%
          </div>
          <div className="text-[10px] sm:text-xs text-gray-500 leading-tight">of monthly<br className="sm:hidden" /> capacity</div>
        </div>
      </div>

      {monthly > 0 ? (
        <>
          <div className="relative h-6 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full ${barColor(utilMonth)} transition-all duration-500`}
              style={{ width: `${widthMonth}%` }}
              data-testid={`capacity-progress-bar-${row.plant_id}`}
            />
            {expectedMarkerPct > 0 && expectedMarkerPct < 100 && (
              <div
                className="absolute top-0 h-full border-l-2 border-dashed border-gray-600"
                style={{ left: `${expectedMarkerPct}%` }}
                title={`Expected by today: ${fmtMt(expected)} MT`}
              />
            )}
          </div>
          <div className="mt-2 grid grid-cols-3 gap-1.5 sm:gap-2 text-[11px] sm:text-xs">
            <div>
              <div className="text-gray-500">Actual MTD</div>
              <div className="font-semibold text-gray-900 tabular-nums">
                {fmtMt(mtd)} MT
              </div>
            </div>
            <div>
              <div className="text-gray-500">Capacity</div>
              <div className="font-semibold text-gray-900 tabular-nums">
                {fmtMt(monthly)} MT
              </div>
            </div>
            <div className="text-right">
              <div className="text-gray-500">vs Expected</div>
              <div
                className={`font-semibold tabular-nums ${
                  utilExpected >= 100 ? 'text-emerald-600' : 'text-red-600'
                }`}
              >
                {utilExpected.toFixed(1)}%
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
          Annual capacity not configured for this plant. Set it in{' '}
          <span className="font-semibold">Plant Master</span> to see the
          progress bar.
          <div className="mt-1 text-xs text-gray-600">
            Actual MTD Production: <strong>{fmtMt(mtd)} MT</strong>
          </div>
        </div>
      )}
    </div>
  );
};

const CapacityProgressPanel = ({ dashboardPlant, selectedYear, selectedMonth, token }) => {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const plantId = dashboardPlant?.id || 'all';

  const load = useCallback(async () => {
    if (!selectedYear || !selectedMonth) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/capacity-progress/${plantId}/${selectedYear}/${selectedMonth}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRows(res.data.plants || []);
    } catch (_e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [plantId, selectedYear, selectedMonth, token]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return null;
  if (!rows || rows.length === 0) return null;

  return (
    <div
      className="bg-white rounded-lg shadow-md p-4 sm:p-5"
      data-testid="capacity-progress-panel"
    >
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center">
          <Gauge className="h-5 w-5 mr-2 text-[#156ACA]" />
          Capacity vs Actual MTD
        </h3>
        <span className="text-[11px] sm:text-xs text-gray-500">
          Dashed = expected by today
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
        {rows.map((row) => (
          <PlantProgressCard key={row.plant_id} row={row} />
        ))}
      </div>
    </div>
  );
};

export default CapacityProgressPanel;
